import React, { useState, useEffect, useRef } from "react";
import "../Terms2.css";

export default function TermsAndPolicy() {
  const ourStore = "Wear & Wonder Boutique LLC";

  const sections = [
    { id: "overview", title: "Overview" },
    { id: "acceptance", title: "Acceptance of Terms" },
    { id: "website-use", title: "Use of Our Website" },
    { id: "account", title: "Account Registration" },
    { id: "privacy", title: "Privacy Policy" },
    { id: "game", title: "Wear & Win Game" },
    { id: "billing", title: "Billing & Account Info" },
    { id: "third-party", title: "Third-Party Links" },
    { id: "liability", title: "Limitation of Liability" },
    { id: "indemnification", title: "Indemnification" },
    { id: "law", title: "Governing Law" },
    
    { id: "changes", title: "Changes" },
    { id: "contact", title: "Contact" },
  ];

  const [openSection, setOpenSection] = useState(null);
  const [activeSection, setActiveSection] = useState("overview");

  const sectionRefs = useRef({});

  useEffect(() => {
    const handleScroll = () => {
      const scrollPos = window.scrollY + 150; // Offset for sticky TOC
      for (let sec of sections) {
        const elem = sectionRefs.current[sec.id];
        if (elem && elem.offsetTop <= scrollPos && elem.offsetTop + elem.offsetHeight > scrollPos) {
          setActiveSection(sec.id);
          break;
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sections]);

  const toggleSection = (id) => {
    setOpenSection(openSection === id ? null : id);
  };

  return (
    <div className="terms-fancy-container">
      {/* Sticky Table of Contents */}
      {/* <aside className="toc">
        <h3>Contents</h3>
        <ul>
          {sections.map((sec) => (
            <li key={sec.id} className={activeSection === sec.id ? "active" : ""}>
              <a
                href={`#${sec.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  sectionRefs.current[sec.id].scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              >
                {sec.title}
              </a>
            </li>
          ))}
        </ul>
      </aside> */}

      {/* Main Content */}
      <main id="MainContent" className="terms-main">
        <h3>Terms of Service & Privacy Policy</h3>
        <p className="last-updated">Last Updated: 8/18/2025</p>

        {sections.map((sec) => (
          <section
            key={sec.id}
            id={sec.id}
            className="collapsible-section"
            ref={(el) => (sectionRefs.current[sec.id] = el)}
          >
            <h2 onClick={() => toggleSection(sec.id)} className="section-title">
              {sec.title} <span>{openSection === sec.id ? "-" : "+"}</span>
            </h2>
            <div
              className={`section-content ${openSection === sec.id ? "open" : ""}`}
              style={{
                maxHeight: openSection === sec.id ? "1200px" : "0px",
                transition: "max-height 0.5s ease",
                overflow: "hidden",
              }}
            >
              <div className={`content-inner ${openSection === sec.id ? "fade-in" : "fade-out"}`}>
                {/* Section content */}
                {sec.id === "overview" && (
                  <>
                    <p>
                      Welcome to <strong>{ourStore}</strong>, your destination for premium apparel, accessories, and curated fashion experiences. By accessing, browsing, or making purchases on our website, you agree to comply with and be bound by these Terms of Service and Privacy Policy. These agreements constitute a legally binding contract between you and {ourStore}.
                    </p>
                    <p>
                      Our goal is to provide a secure, transparent, and enjoyable shopping experience. These Terms clarify your rights, obligations, and responsibilities while interacting with our platform and services.
                    </p>
                  </>
                )}
                {sec.id === "acceptance" && (
                  <>
                    <p>
                      By using our website or services, you explicitly agree to these Terms and our Privacy Policy. If you do not accept any part of these Terms, you must discontinue using our website and services immediately.
                    </p>
                    <p>
                      We reserve the right to update these Terms at any time. Continued use of the site after updates constitutes your acceptance of the revised Terms.
                    </p>
                  </>
                )}
                {sec.id === "website-use" && (
                  <>
                    <p>
                      Users must be at least 18 years old or have obtained parental or guardian consent to interact with this website. Minors are not permitted to purchase or participate without explicit consent.
                    </p>
                    <p>
                      You agree not to misuse our site, interfere with its operation, or engage in illegal activity. Uploading harmful code, unauthorized scripts, or attempting security breaches is strictly prohibited.
                    </p>
                  </>
                )}
                {sec.id === "account" && (
                  <>
                    <p>
                      Certain features of our website require account registration. You are responsible for maintaining the confidentiality of your account credentials and all activity under your account. Any fraudulent or unauthorized use must be reported immediately.
                    </p>
                    <p>
                      You agree to provide accurate, complete, and current information. Failure to do so may result in termination of access or denial of services.
                    </p>
                  </>
                )}
                {sec.id === "privacy" && (
                  <>
                    <h3>Information Collection & Use</h3>
                    <ul>
                      <li>Personal identification details including name, email, phone number, shipping address, and optional demographic info.</li>
                      <li>Account credentials to provide secure access to services.</li>
                      <li>Order history, billing information, and payment status to fulfill purchases.</li>
                      <li>Site usage, cookies, and tracking to enhance user experience and improve site performance.</li>
                      <li>Communications with customer service to resolve inquiries efficiently.</li>
                    </ul>
                    <h3>Cookies & Tracking</h3>
                    <ul>
                      <li>We use cookies and analytics to understand site behavior, improve navigation, and provide personalized recommendations.</li>
                      <li>Tracking may include device information, IP address, browser type, pages visited, and click patterns.</li>
                    </ul>
                    <h3>Data Sharing</h3>
                    <ul>
                      <li>Trusted service providers, including shipping and payment processors, may receive information solely for order fulfillment.</li>
                      <li>Marketing partners may be engaged for promotional communications, but only with consent.</li>
                      <li>Legal authorities, if required by law, may access user information.</li>
                    </ul>
                    <p>
                      Users have the right to access, update, or request deletion of personal information at any time. Data is retained only for the duration necessary to provide services or comply with legal obligations.
                    </p>
                  </>
                )}
                {sec.id === "game" && (
                  <>
                    <p>
                      By signing up on our website, your email is automatically entered into our monthly <strong>“Wear & Win”</strong> game. Participation does not require any purchase.
                    </p>
                    <p><strong>Winner Selection:</strong> A single email is randomly chosen each month using an unbiased algorithm to ensure fairness and randomness.</p>
                    <p><strong>Prize Details:</strong> Winners receive a randomly selected item, which may vary in color, size, or design. No customization requests can be accommodated.</p>
                    <p><strong>Claiming Your Prize:</strong> Winners can log in to the website, navigate to the Wear & Win tab, and claim their prize by providing a shipping address. If a winner chooses not to claim their prize, they may opt out, and the prize will remain unclaimed for that period.</p>
                    <p>
                      Participation is voluntary. The game is conducted in compliance with Washington State gaming regulations and federal law. By participating, users consent to these rules and acknowledge that the prize is final.
                    </p>
                  </>
                )}
                {sec.id === "billing" && (
                  <>
                    <p>
                      Users must provide accurate billing and account information. We reserve the right to limit, suspend, or cancel orders where there is suspicion of fraud, illegal activity, or policy violations.
                    </p>
                    <p>
                      Any payment disputes or discrepancies should be reported immediately. Refunds and cancellations are handled according to our refund policy.
                    </p>
                  </>
                )}
                {sec.id === "third-party" && (
                  <>
                    <p>
                      Our site may include links to third-party websites. We are not responsible for their content, privacy practices, or compliance. Users should review third-party policies before engaging.
                    </p>
                  </>
                )}
                {sec.id === "liability" && (
                  <>
                    <p>
                      Our services and products are provided "as is." {ourStore} is not liable for direct, indirect, incidental, consequential, or punitive damages arising from use, including loss of data, profits, or personal injury.
                    </p>
                  </>
                )}
                {sec.id === "indemnification" && (
                  <>
                    <p>
                      Users agree to indemnify, defend, and hold harmless {ourStore}, its affiliates, and partners against any claims, liabilities, damages, or costs resulting from user actions or violations of these Terms.
                    </p>
                  </>
                )}
                {sec.id === "law" && (
                  <>
                    <p>
                      These Terms are governed by and construed in accordance with the laws of the State of Washington, USA. Users consent to jurisdiction in King County, WA for any legal proceedings.
                    </p>
                  </>
                )}
                {sec.id === "changes" && (
                  <>
                    <p>
                      We may update these Terms and the Privacy Policy at any time. Significant changes will be reflected with a revised “Last Updated” date. Continued use of our services constitutes acceptance of the updated Terms.
                    </p>
                  </>
                )}
                {sec.id === "contact" && (
                  <>
                    <p>Email: <a href="mailto:wearandwonderboutique@gmail.com">wearandwonderboutique@gmail.com</a></p>

                  </>
                )}
              </div>
            </div>
          </section>
        ))}
      </main>
    </div>
  );
}
